import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { useWebSocket } from '../hooks/useWebSocket';
import {
  generateKeyPair,
  exportPublicKey,
  importPublicKey,
  deriveSharedKey,
  encryptMessage,
  decryptMessage,
  getKeyFingerprint,
} from '../utils/crypto';
import { Terminal, Shield, ShieldOff, Copy, ArrowLeft, Send, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';

export default function ChatRoom() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  
  // All state in RAM only - never persisted
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [keyPair, setKeyPair] = useState(null);
  const [sharedKey, setSharedKey] = useState(null);
  const [peerConnected, setPeerConnected] = useState(false);
  const [myFingerprint, setMyFingerprint] = useState('');
  const [peerFingerprint, setPeerFingerprint] = useState('');
  const [isEncrypted, setIsEncrypted] = useState(false);
  
  const messagesEndRef = useRef(null);
  const pendingKeyExchange = useRef(false);
  const sendRef = useRef(null);
  const keyPairRef = useRef(null);
  const sharedKeyRef = useRef(null);

  // Keep refs in sync with state
  useEffect(() => {
    keyPairRef.current = keyPair;
  }, [keyPair]);

  useEffect(() => {
    sharedKeyRef.current = sharedKey;
  }, [sharedKey]);

  // Handle incoming WebSocket messages
  const handleMessage = useCallback(async (data) => {
    if (data.type === 'connected') {
      if (data.user_count > 1) {
        setPeerConnected(true);
      }
    } else if (data.type === 'user_joined') {
      setPeerConnected(true);
      toast.success('Peer connected');
      
      // If we have a key pair, send our public key
      if (keyPairRef.current && !pendingKeyExchange.current && sendRef.current) {
        pendingKeyExchange.current = true;
        const pubKeyBase64 = await exportPublicKey(keyPairRef.current.publicKey);
        sendRef.current(JSON.stringify({
          type: 'key_exchange',
          publicKey: pubKeyBase64,
        }));
      }
    } else if (data.type === 'user_left') {
      setPeerConnected(false);
      setSharedKey(null);
      setIsEncrypted(false);
      setPeerFingerprint('');
      pendingKeyExchange.current = false;
      toast.error('Peer disconnected - encryption disabled');
      
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'system',
        text: 'Peer disconnected. Encryption keys destroyed.',
        timestamp: new Date(),
      }]);
    } else if (data.type === 'key_exchange') {
      try {
        const peerPublicKey = await importPublicKey(data.publicKey);
        const derived = await deriveSharedKey(keyPairRef.current.privateKey, peerPublicKey);
        setSharedKey(derived);
        setIsEncrypted(true);
        
        const fp = await getKeyFingerprint(peerPublicKey);
        setPeerFingerprint(fp);
        
        // If we haven't sent our key yet, send it now
        if (!pendingKeyExchange.current && sendRef.current) {
          pendingKeyExchange.current = true;
          const pubKeyBase64 = await exportPublicKey(keyPairRef.current.publicKey);
          sendRef.current(JSON.stringify({
            type: 'key_exchange',
            publicKey: pubKeyBase64,
          }));
        }
        
        toast.success('End-to-end encryption established');
        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'system',
          text: 'E2E encryption active. Messages are secure.',
          timestamp: new Date(),
        }]);
      } catch (e) {
        console.error('Key exchange failed:', e);
        toast.error('Key exchange failed');
      }
    } else if (data.type === 'encrypted_message') {
      if (sharedKeyRef.current) {
        try {
          const plaintext = await decryptMessage(sharedKeyRef.current, {
            iv: data.iv,
            ciphertext: data.ciphertext,
          });
          setMessages(prev => [...prev, {
            id: Date.now(),
            type: 'received',
            text: plaintext,
            timestamp: new Date(),
          }]);
        } catch (e) {
          console.error('Decryption failed:', e);
          setMessages(prev => [...prev, {
            id: Date.now(),
            type: 'error',
            text: '[Decryption failed]',
            timestamp: new Date(),
          }]);
        }
      }
    }
  }, []);

  const { status, userCount, send: wsSend } = useWebSocket(roomId, handleMessage);
  
  // Keep send ref in sync
  useEffect(() => {
    sendRef.current = wsSend;
  }, [wsSend]);

  // Generate ephemeral key pair on mount
  useEffect(() => {
    const initCrypto = async () => {
      try {
        const kp = await generateKeyPair();
        setKeyPair(kp);
        const fp = await getKeyFingerprint(kp.publicKey);
        setMyFingerprint(fp);
      } catch (e) {
        console.error('Failed to generate keys:', e);
        toast.error('Failed to initialize encryption');
      }
    };
    initCrypto();
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send encrypted message
  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim()) return;
    
    if (!isEncrypted || !sharedKey) {
      toast.error('Waiting for encryption - peer must connect first');
      return;
    }
    
    try {
      const encrypted = await encryptMessage(sharedKey, inputMessage);
      
      wsSend({
        type: 'encrypted_message',
        iv: encrypted.iv,
        ciphertext: encrypted.ciphertext,
      });
      
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'sent',
        text: inputMessage,
        timestamp: new Date(),
      }]);
      
      setInputMessage('');
    } catch (e) {
      console.error('Failed to send:', e);
      toast.error('Failed to encrypt message');
    }
  };

  const copyLink = () => {
    const link = `${window.location.origin}/room/${roomId}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard');
  };

  const leaveRoom = () => {
    navigate('/');
  };

  const getStatusColor = () => {
    if (status === 'connected' && isEncrypted) return 'text-green-500';
    if (status === 'connected') return 'text-yellow-500';
    if (status === 'connecting') return 'text-yellow-500';
    return 'text-red-500';
  };

  const getStatusText = () => {
    if (status === 'connected' && isEncrypted) return 'ENCRYPTED';
    if (status === 'connected') return 'CONNECTED';
    if (status === 'connecting') return 'CONNECTING...';
    if (status === 'error') return 'ERROR';
    return 'DISCONNECTED';
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      {/* Noise overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.03] z-50"
           style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }} />

      {/* Header - Mobile optimized */}
      <header className="border-b-2 border-white/10 p-3 sm:p-4 bg-[#0A0A0A] sticky top-0 z-40">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="sm"
              onClick={leaveRoom}
              className="text-zinc-400 hover:text-white hover:bg-white/10 rounded-sm p-2 sm:px-3"
            >
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="font-mono text-xs hidden sm:inline">EXIT</span>
            </Button>
            
            <div className="flex items-center gap-2">
              <Terminal className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className="font-mono font-bold text-sm tracking-tight hidden sm:inline">GHOSTTALK</span>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            <div className="flex items-center gap-1 sm:gap-2 text-zinc-400">
              <Users className="w-4 h-4" />
              <span className="font-mono text-xs">{userCount}/2</span>
            </div>
            
            <div className={`flex items-center gap-1 sm:gap-2 ${getStatusColor()}`}>
              {isEncrypted ? <Shield className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
              <span className="font-mono text-[10px] sm:text-xs font-bold">{getStatusText()}</span>
            </div>

            <Button
              data-testid="copy-link-btn"
              variant="ghost"
              size="sm"
              onClick={copyLink}
              className="text-zinc-400 hover:text-white hover:bg-white/10 rounded-sm p-2 sm:px-3"
            >
              <Copy className="w-4 h-4 sm:mr-2" />
              <span className="font-mono text-xs hidden sm:inline">COPY LINK</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Room info bar - Mobile optimized */}
      <div className="border-b border-white/5 bg-[#080808] px-3 sm:px-4 py-2 overflow-x-auto">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4 text-xs font-mono">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <span className="text-zinc-600 flex-shrink-0">ROOM:</span>
            <code className="text-zinc-400 bg-white/5 px-2 py-0.5 truncate max-w-[200px] sm:max-w-none" data-testid="room-id">{roomId}</code>
          </div>
          {myFingerprint && (
            <div className="flex items-center gap-2 sm:gap-4 text-zinc-600 text-[10px] sm:text-xs">
              <span className="flex-shrink-0">KEY: <code className="text-green-500/70">{myFingerprint}</code></span>
              {peerFingerprint && (
                <span className="flex-shrink-0 hidden sm:inline">PEER: <code className="text-green-500/70">{peerFingerprint}</code></span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Messages area */}
      <main className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="max-w-4xl mx-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center py-16 space-y-4">
                <div className="inline-flex items-center gap-2 text-zinc-600 font-mono text-sm">
                  <span className="animate-pulse">_</span>
                  <span>Waiting for messages...</span>
                </div>
                {!peerConnected && (
                  <p className="text-zinc-700 text-xs font-mono">
                    Share the link to start an encrypted conversation
                  </p>
                )}
              </div>
            )}

            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.type === 'sent' ? 'justify-end' : msg.type === 'received' ? 'justify-start' : 'justify-center'}`}
              >
                {msg.type === 'system' || msg.type === 'error' ? (
                  <div className={`text-xs font-mono ${msg.type === 'error' ? 'text-red-500' : 'text-zinc-600'} bg-white/5 px-3 py-1`}>
                    {msg.text}
                  </div>
                ) : (
                  <div
                    data-testid={`message-${msg.type}`}
                    className={`max-w-[70%] px-4 py-2 font-mono text-sm ${
                      msg.type === 'sent'
                        ? 'bg-green-500/20 text-green-100 border border-green-500/30'
                        : 'bg-white/5 text-zinc-200 border border-white/10'
                    }`}
                  >
                    <p className="break-words">{msg.text}</p>
                    <p className="text-[10px] text-zinc-600 mt-1">
                      {msg.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </main>

      {/* Input area - Mobile optimized with safe area */}
      <footer className="border-t-2 border-white/10 p-3 sm:p-4 bg-[#0A0A0A] pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:pb-[calc(1rem+env(safe-area-inset-bottom))]">
        <form onSubmit={sendMessage} className="max-w-4xl mx-auto flex gap-2 sm:gap-3">
          <Input
            data-testid="message-input"
            type="text"
            placeholder={isEncrypted ? "Type message..." : "Waiting for encryption..."}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            disabled={!isEncrypted}
            className="flex-1 bg-transparent border-2 border-white/10 rounded-none font-mono text-sm placeholder:text-zinc-700 focus:border-green-500/50 focus:ring-0 disabled:opacity-50 min-h-[44px]"
          />
          <Button
            data-testid="send-btn"
            type="submit"
            disabled={!isEncrypted || !inputMessage.trim()}
            className="bg-green-500 text-black font-mono font-bold rounded-sm hover:bg-white transition-colors duration-150 disabled:opacity-30 disabled:hover:bg-green-500 px-3 sm:px-4 min-h-[44px]"
          >
            <Send className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">SEND</span>
          </Button>
        </form>
        
        <div className="max-w-4xl mx-auto mt-2 sm:mt-3 flex items-center justify-center gap-2 text-[9px] sm:text-[10px] font-mono text-zinc-700">
          <Trash2 className="w-3 h-3 flex-shrink-0" />
          <span className="text-center">NEVER stored • Refresh = Gone</span>
        </div>
      </footer>
    </div>
  );
}
